import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { Fragment, ChangeEvent, useCallback } from 'react'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  DocusignDisabledFields,
  DocusignFieldNames,
  DocusignFieldPlaceholders,
  DocusignFieldSelectOptions,
  DocusignFieldTabLabel,
  DocusignTaxClassificationLabelMap,
  DocusignVehicleTypeLabelMap,
  TaxClassificationType,
  BankAccountType,
  DocusignBankAccountLabelMap,
  DocusignPolicyDocumentDeliveryLabelMap,
  PolicyDocumentDeliveryPreference,
} from 'src/constants/docusign'
import { DocusignRadioOption, DocusignSigningField } from 'src/interfaces/docusign'
import tw from 'twin.macro'
import { FormikCheckbox } from '../shared/form/formik/FormikCheckbox'
import { FormikRadioGroup } from '../shared/form/formik/FormikRadioGroup'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { DocusignFieldsFormModalValues } from './DocusignFieldsFormModal.interface'
import React from 'react'
import { InfoContainer } from '../shared/InfoContainer'

const tabLabelToKebab = (tabLabel: string) =>
  tabLabel.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

const docusignFieldInputTestId = (tabLabel: string) =>
  `contractors-onboarding-docusign-field-${tabLabelToKebab(tabLabel)}-input`

const masks = {
  [DocusignFieldTabLabel.phoneNumber]: undefined,
  [DocusignFieldTabLabel.phoneNumberHome]: undefined,
  [DocusignFieldTabLabel.phoneNumberWork]: undefined,
  [DocusignFieldTabLabel.email]: undefined,
  [DocusignFieldTabLabel.fullName]: undefined,
  [DocusignFieldTabLabel.firstName]: undefined,
  [DocusignFieldTabLabel.lastName]: undefined,
  [DocusignFieldTabLabel.mi]: undefined,
  [DocusignFieldTabLabel.maidenName]: undefined,
  [DocusignFieldTabLabel.maidenNameLastYearUsed]: [/\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.driverNumber]: undefined,
  [DocusignFieldTabLabel.contractorMarketName]: undefined,
  [DocusignFieldTabLabel.address]: undefined,
  [DocusignFieldTabLabel.city]: undefined,
  [DocusignFieldTabLabel.state]: undefined,
  [DocusignFieldTabLabel.county]: undefined,
  [DocusignFieldTabLabel.zipcode]: [/\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.cityStateZipcode]: undefined,
  [DocusignFieldTabLabel.prevAddress1]: undefined,
  [DocusignFieldTabLabel.prevCity1]: undefined,
  [DocusignFieldTabLabel.prevState1]: undefined,
  [DocusignFieldTabLabel.prevZipcode1]: [/\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.prevAddress2]: undefined,
  [DocusignFieldTabLabel.prevCity2]: undefined,
  [DocusignFieldTabLabel.prevState2]: undefined,
  [DocusignFieldTabLabel.prevZipcode2]: [/\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.ssn]: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.ssnLast4]: [/\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.tin]: ['9', /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.fein]: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.dob]: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.beneficiary]: undefined,
  [DocusignFieldTabLabel.beneficiaryRelationship]: undefined,
  [DocusignFieldTabLabel.beneficiaryFirstName]: undefined,
  [DocusignFieldTabLabel.beneficiaryLastName]: undefined,
  [DocusignFieldTabLabel.beneficiaryMI]: undefined,
  [DocusignFieldTabLabel.beneficiaryAddress]: undefined,
  [DocusignFieldTabLabel.beneficiaryDeathToEstateCheckbox]: undefined,
  [DocusignFieldTabLabel.insuranceCarrier]: undefined,
  [DocusignFieldTabLabel.otherInsurancePlan]: undefined,
  [DocusignFieldTabLabel.eSignatureAck]: undefined,
  [DocusignFieldTabLabel.applicantElectronicSigningAck]: undefined,
  [DocusignFieldTabLabel.policyDocumentDeliveryPreference]: undefined,
  [DocusignFieldTabLabel.businessName]: undefined,
  [DocusignFieldTabLabel.businessAddress]: undefined,
  [DocusignFieldTabLabel.ein]: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.vehicleYear]: [/\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.vehicleMake]: undefined,
  [DocusignFieldTabLabel.vehicleModel]: undefined,
  [DocusignFieldTabLabel.vehicleVIN]: undefined,
  [DocusignFieldTabLabel.vehicleLicensePlate]: undefined,
  [DocusignFieldTabLabel.gvw]: undefined,
  [DocusignFieldTabLabel.vehicleType]: undefined,
  [DocusignFieldTabLabel.vehicleTypeOther]: undefined,
  [DocusignFieldTabLabel.vehicleTypeRequired]: undefined,
  [DocusignFieldTabLabel.vehicleTypeOtherRequired]: undefined,
  [DocusignFieldTabLabel.vehicleYear2]: [/\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.vehicleMake2]: undefined,
  [DocusignFieldTabLabel.vehicleModel2]: undefined,
  [DocusignFieldTabLabel.vehicleVIN2]: undefined,
  [DocusignFieldTabLabel.vehicleLicensePlate2]: undefined,
  [DocusignFieldTabLabel.gvw2]: undefined,
  [DocusignFieldTabLabel.vehicleType2]: undefined,
  [DocusignFieldTabLabel.vehicleTypeOther2]: undefined,
  [DocusignFieldTabLabel.vehicleYear3]: [/\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.vehicleMake3]: undefined,
  [DocusignFieldTabLabel.vehicleModel3]: undefined,
  [DocusignFieldTabLabel.vehicleVIN3]: undefined,
  [DocusignFieldTabLabel.vehicleLicensePlate3]: undefined,
  [DocusignFieldTabLabel.gvw3]: undefined,
  [DocusignFieldTabLabel.vehicleType3]: undefined,
  [DocusignFieldTabLabel.vehicleTypeOther3]: undefined,
  [DocusignFieldTabLabel.taxClassificationType]: undefined,
  [DocusignFieldTabLabel.llcType]: undefined,
  [DocusignFieldTabLabel.exemptPayeeCode]: undefined,
  [DocusignFieldTabLabel.exemptionFatcaCode]: undefined,
  [DocusignFieldTabLabel.mmsOregonAddress]: undefined,
  [DocusignFieldTabLabel.driverLicenseNumber]: undefined,
  [DocusignFieldTabLabel.yearsOfExperience]: undefined,
  [DocusignFieldTabLabel.driverLicenseState]: undefined,
  [DocusignFieldTabLabel.otherForeignPartners]: undefined,
  [DocusignFieldTabLabel.otherTaxClassification]: undefined,
  [DocusignFieldTabLabel.inselAgreementCheckbox]: undefined,
  [DocusignFieldTabLabel.taxId]: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.companyTitle]: undefined,
  [DocusignFieldTabLabel.smartDeliveryCheckboxLease]: undefined,
  [DocusignFieldTabLabel.smartDeliveryCheckboxAdAck]: undefined,
  [DocusignFieldTabLabel.smartDeliveryCheckboxShirt]: undefined,
  [DocusignFieldTabLabel.smartDeliveryCheckboxIdBadge]: undefined,
  [DocusignFieldTabLabel.smartDeliveryCheckboxDecal]: undefined,
  [DocusignFieldTabLabel.addressSinceDate]: [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.prevAddressSinceDate1]: [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.prevAddressSinceDate2]: [/\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.w9FullName]: undefined,
  [DocusignFieldTabLabel.w9BusinessName]: undefined,
  [DocusignFieldTabLabel.w9Address]: undefined,
  [DocusignFieldTabLabel.w9City]: undefined,
  [DocusignFieldTabLabel.w9State]: undefined,
  [DocusignFieldTabLabel.w9County]: undefined,
  [DocusignFieldTabLabel.w9Zipcode]: [/\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.w9Ssn]: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.w9Ein]: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.bankName]: undefined,
  [DocusignFieldTabLabel.bankRoutingNumber]: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
  [DocusignFieldTabLabel.bankAccountNumber]: [
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    /\d/,
  ],
  [DocusignFieldTabLabel.bankAccountType]: undefined,
}

interface Props {
  field: DocusignSigningField
}

const selectFields = [
  DocusignFieldTabLabel.state,
  DocusignFieldTabLabel.driverLicenseState,
  DocusignFieldTabLabel.llcType,
  DocusignFieldTabLabel.w9State,
]
const radioLabelMaps: any = {
  [DocusignFieldTabLabel.taxClassificationType]: DocusignTaxClassificationLabelMap,
  [DocusignFieldTabLabel.vehicleType]: DocusignVehicleTypeLabelMap,
  [DocusignFieldTabLabel.vehicleTypeRequired]: DocusignVehicleTypeLabelMap,
  [DocusignFieldTabLabel.bankAccountType]: DocusignBankAccountLabelMap,
  [DocusignFieldTabLabel.policyDocumentDeliveryPreference]: DocusignPolicyDocumentDeliveryLabelMap,
}

export const DocusignFieldsFormModalInput = ({ field }: Props) => {
  const { handleChange, setFieldValue } = useFormikContext<DocusignFieldsFormModalValues>()

  const handleRadioChange = useCallback(
    (name: string) => (event: ChangeEvent) => {
      handleChange(event)

      if (name === DocusignFieldTabLabel.taxClassificationType) {
        setFieldValue(DocusignFieldTabLabel.llcType, '')
      }

      if (name === DocusignFieldTabLabel.vehicleType) {
        setFieldValue(DocusignFieldTabLabel.vehicleTypeOther, '')
      }

      if (name === DocusignFieldTabLabel.vehicleTypeRequired) {
        setFieldValue(DocusignFieldTabLabel.vehicleTypeOtherRequired, '')
      }
    },
    [],
  )

  const handleCheckboxChange = useCallback(
    (name: string) => (event: ChangeEvent<HTMLInputElement>) => {
      handleChange(event)

      if (name === DocusignFieldTabLabel.smartDeliveryCheckboxAdAck) {
        setFieldValue(DocusignFieldTabLabel.smartDeliveryCheckboxShirt, event.currentTarget.checked)
        setFieldValue(
          DocusignFieldTabLabel.smartDeliveryCheckboxIdBadge,
          event.currentTarget.checked,
        )
        setFieldValue(DocusignFieldTabLabel.smartDeliveryCheckboxDecal, event.currentTarget.checked)
      }
    },
    [],
  )

  if (selectFields.includes(field.tabLabel) && DocusignFieldSelectOptions[field.tabLabel]) {
    return (
      <div css={tw`w-full pb-4 pt-2`}>
        <FormikSelect
          name={field.tabLabel}
          label={`${DocusignFieldNames[field.tabLabel]}${field.required ? '*' : ''}`}
          placeholder={DocusignFieldPlaceholders[field.tabLabel]}
          options={DocusignFieldSelectOptions[field.tabLabel]}
          inputProps={{ 'data-testid': docusignFieldInputTestId(field.tabLabel) }}
        />
      </div>
    )
  }

  if (field.tabType === 'radiogroup') {
    const labelMap = radioLabelMaps[field.tabLabel]
    let radioOptions = field.radios

    if (field.tabLabel === DocusignFieldTabLabel.taxClassificationType) {
      radioOptions = [
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.Individual,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.CCorp,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.SCorp,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.Partnership,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.Trust,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.Llc,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === TaxClassificationType.Other,
        ) as DocusignRadioOption,
      ]
    }

    if (field.tabLabel === DocusignFieldTabLabel.bankAccountType) {
      radioOptions = [
        radioOptions.find(
          (radio) => radio.value === BankAccountType.Checking,
        ) as DocusignRadioOption,
        radioOptions.find(
          (radio) => radio.value === BankAccountType.Savings,
        ) as DocusignRadioOption,
      ]
    }

    if (field.tabLabel === DocusignFieldTabLabel.policyDocumentDeliveryPreference) {
      const electronic = field.radios.find(
        (radio) => radio.value === PolicyDocumentDeliveryPreference.Electronic,
      )
      const notElectronic = field.radios.find(
        (radio) => radio.value === PolicyDocumentDeliveryPreference.NotElectronic,
      )

      if (electronic && notElectronic) {
        radioOptions = [electronic, notElectronic]
      }
    }

    return (
      <div css={tw`w-full pb-4 pt-2`}>
        {field.tabLabel === DocusignFieldTabLabel.taxClassificationType && (
          <InfoContainer className={tw`mb-4`} color="orange">
            Single-Member LLCs should select "Individual / Sole Proprietor" unless their LLC is
            taxed as an S Corporation or C Corporation.
          </InfoContainer>
        )}
        <FormikRadioGroup
          name={field.tabLabel}
          label={`${DocusignFieldNames[field.tabLabel]}${
            field.required || field.tabLabel === DocusignFieldTabLabel.vehicleTypeRequired
              ? '*'
              : ''
          }`}
          options={radioOptions.map((radio) => ({
            label: labelMap ? labelMap[radio.value] : radio.value,
            value: radio.value,
          }))}
          onChange={handleRadioChange(field.tabLabel)}
          dataTestId={docusignFieldInputTestId(field.tabLabel)}
        />
      </div>
    )
  }

  if (field.tabType === 'checkbox') {
    if (field.tabLabel === DocusignFieldTabLabel.inselAgreementCheckbox) {
      return (
        <div css={tw`pb-4 pt-2`}>
          <FormikCheckbox
            css={tw`w-full`}
            name={field.tabLabel}
            inputProps={{ 'data-testid': docusignFieldInputTestId(field.tabLabel) }}
            label={
              <span>
                {' '}
                I have read, agree and accept Terms and Conditions per{' '}
                <a
                  href="https://storage.googleapis.com/public_agreements_prod/Membership%20Agreement.pdf"
                  target="_blank"
                  data-testid={`${docusignFieldInputTestId(field.tabLabel)}-membership-link`}
                >
                  Membership Agreement
                </a>
              </span>
            }
          />
        </div>
      )
    }

    return (
      <Fragment>
        {field.tabLabel === DocusignFieldTabLabel.beneficiaryDeathToEstateCheckbox && (
          <Typography variant="h5" css={tw`my-2 text-center text-shades-400`}>
            OR
          </Typography>
        )}
        <div css={tw`pb-4 pt-2`}>
          <FormikCheckbox
            css={tw`w-full`}
            name={field.tabLabel}
            label={`${DocusignFieldNames[field.tabLabel]}${field.required ? '*' : ''}`}
            disabled={DocusignDisabledFields.includes(field.tabLabel)}
            onChange={handleCheckboxChange(field.tabLabel)}
            inputProps={{ 'data-testid': docusignFieldInputTestId(field.tabLabel) }}
          />
        </div>
      </Fragment>
    )
  }

  return (
    <Fragment>
      {field.tabLabel === DocusignFieldTabLabel.otherTaxClassification ? (
        <div css={tw`mb-4 mt-2`}>
          <Typography variant="body1" css={tw`text-shades-400`} fontWeight={500}>
            Go to{' '}
            <a href="https://www.irs.gov/FormW9" target="_blank">
              www.irs.gov/FormW9
            </a>{' '}
            for instructions
          </Typography>
        </div>
      ) : null}
      <div css={tw`w-full pb-4 pt-2`}>
        <FormikTextField
          name={field.tabLabel}
          css={tw`w-full`}
          label={`${DocusignFieldNames[field.tabLabel]}${field.required ? '*' : ''}`}
          mask={masks[field.tabLabel]}
          placeholder={DocusignFieldPlaceholders[field.tabLabel]}
          inputProps={{ 'data-testid': docusignFieldInputTestId(field.tabLabel) }}
        />
        {field.tabLabel === DocusignFieldTabLabel.dob ? (
          <InfoContainer className={tw`mt-2`} color="orange">
            Use the Month/Day/Year format.
          </InfoContainer>
        ) : null}
      </div>
    </Fragment>
  )
}
